import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Calendar, Eye, MapPin, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const locality = params.get("locality") || "";

  useEffect(() => {
    document.title = "Krishna Property Blog — Surat Real Estate Insights";
    setLoading(true);
    api.get("/blog", { params: locality ? { locality } : {} }).then((r) => setPosts(r.data)).finally(() => setLoading(false));
  }, [locality]);

  return (
    <div className="max-w-[1200px] mx-auto px-4 py-10" data-testid="blog-page">
      <div className="mb-8">
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900">Krishna Property <span className="text-kp-maroon">Blog</span></h1>
        <p className="text-slate-500 mt-2 max-w-2xl">Insights on the Surat real estate market — price trends, neighbourhood guides, buying tips, and RERA updates.</p>
        {locality && <div className="mt-3 text-sm text-kp-teal">Filtered by locality: <b>{locality}</b> · <Link to="/blog" className="underline">clear</Link></div>}
      </div>

      {loading ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{Array.from({length:6}).map((_,i) => <div key={i} className="bg-slate-100 rounded-2xl h-72 animate-pulse"/>)}</div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl">
          <p className="text-slate-500">No blog posts yet. Check back soon for market insights!</p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {posts.map((p) => (
            <Link to={`/blog/${p.slug}`} key={p.id} className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-md hover:border-kp-teal transition-all" data-testid={`blog-card-${p.slug}`}>
              {p.cover_image ? (
                <img src={p.cover_image} alt={p.title} className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-500"/>
              ) : (
                <div className="w-full h-44 flex items-center justify-center" style={{ background: "linear-gradient(135deg, var(--kp-teal), rgba(15,94,90,0.6))" }}>
                  <div className="text-white font-display text-2xl px-6 text-center">{p.title.slice(0, 40)}</div>
                </div>
              )}
              <div className="p-5">
                <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                  <span className="flex items-center gap-1"><Calendar size={12}/> {new Date(p.created_at).toLocaleDateString("en-IN", {day:"numeric", month:"short", year:"numeric"})}</span>
                  {p.locality && <span className="flex items-center gap-1 text-kp-maroon"><MapPin size={12}/> {p.locality}</span>}
                  {p.views > 0 && <span className="flex items-center gap-1"><Eye size={12}/> {p.views}</span>}
                </div>
                <h3 className="font-display font-bold text-lg text-slate-900 group-hover:text-kp-teal transition-colors line-clamp-2">{p.title}</h3>
                {p.excerpt && <p className="text-sm text-slate-600 mt-2 line-clamp-3">{p.excerpt}</p>}
                {p.tags?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {p.tags.slice(0, 3).map((t) => <span key={t} className="text-[10px] bg-kp-teal-light text-kp-teal font-semibold px-2 py-0.5 rounded uppercase tracking-wide">{t}</span>)}
                  </div>
                )}
                <div className="mt-4 text-sm font-semibold text-kp-teal flex items-center gap-1 group-hover:gap-2 transition-all">Read more <ArrowRight size={14}/></div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
